import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Truck, Store, MapPin, Clock, Navigation } from "lucide-react";
import { MapLocationSelector } from "./MapLocationSelector";
import { DateTimePicker } from "./DateTimePicker";
import { OrderMode, BranchDto } from "@/types";
import { categories } from "@/data/menuItems";
import { useApp } from "@/App";

interface OrderControlsProps {
  orderMode: OrderMode;
  setOrderMode: (mode: OrderMode) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}

export function OrderControls({
  orderMode,
  setOrderMode,
  selectedCategory,
  setSelectedCategory,
  searchQuery,
  setSearchQuery,
}: OrderControlsProps) {
  const { restaurant, restaurantWithBranches } = useApp();
  const [selectedLocation, setSelectedLocation] = useState<BranchDto | null>(
    restaurantWithBranches?.branches[0] ?? null
  );
  useEffect(() => {
    if (restaurantWithBranches && restaurantWithBranches?.branches.length > 0) {
      setSelectedLocation(restaurantWithBranches.branches[0] ?? null);
    }
  }, [restaurantWithBranches]);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState("ASAP");

  const handleLocationSelect = (location: BranchDto) => {
    setSelectedLocation(location);
    setOrderMode({ ...orderMode, location: location.name });
  };

  const handleDateTimeChange = (date: Date, time: string) => {
    setSelectedDate(date);
    setSelectedTime(time);
    setOrderMode({
      ...orderMode,
      scheduledDate: date.toISOString(),
      scheduledTime: time,
    });
  };

  return (
    <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 py-6 sm:py-8 shadow-sm">
      <div className="max-w-7xl mx-auto mobile-padding">
        <div className="space-y-6 sm:space-y-8">
          {/* Order Type and Location - Enhanced Mobile Layout */}
          <div className="space-y-4 sm:space-y-6">
            {/* Pickup/Delivery Toggle - Enhanced Design */}
            {restaurant?.isDeliveryModeEnabled == true &&
              restaurant?.isPickupModeEnabled == true && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
                      Order Type
                    </h3>
                    {/* <p className="text-sm text-gray-600">
                      Choose pickup or delivery
                    </p> */}
                  </div>

                  <div className="flex bg-white rounded-2xl p-2 shadow-lg border border-gray-200 w-full sm:w-auto">
                    <Button
                      variant={
                        orderMode.type === "pickup" ? "default" : "ghost"
                      }
                      size="lg"
                      className={`rounded-xl px-6 sm:px-8 py-3 font-semibold transition-all duration-300 flex-1 sm:flex-none ${
                        orderMode.type === "pickup"
                          ? "bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg transform scale-105"
                          : "hover:bg-gray-50 text-gray-700 hover:scale-105"
                      }`}
                      onClick={() =>
                        setOrderMode({ ...orderMode, type: "pickup" })
                      }
                    >
                      <Store className="w-5 h-5 mr-2" />
                      <div className="text-left">
                        <div className="text-sm sm:text-base font-semibold">
                          Pickup
                        </div>
                        <div className="text-xs opacity-80">
                          Ready in 15-20 min
                        </div>
                      </div>
                    </Button>
                    <Button
                      variant={
                        orderMode.type === "delivery" ? "default" : "ghost"
                      }
                      size="lg"
                      className={`rounded-xl px-6 sm:px-8 py-3 font-semibold transition-all duration-300 flex-1 sm:flex-none ${
                        orderMode.type === "delivery"
                          ? "bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-lg transform scale-105"
                          : "hover:bg-gray-50 text-gray-700 hover:scale-105"
                      }`}
                      onClick={() =>
                        setOrderMode({ ...orderMode, type: "delivery" })
                      }
                    >
                      <Truck className="w-5 h-5 mr-2" />
                      <div className="text-left">
                        <div className="text-sm sm:text-base font-semibold">
                          Delivery
                        </div>
                        <div className="text-xs opacity-80">25-35 min</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

            {/* Location and Time Selection - Enhanced Mobile Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Location Selector - Enhanced */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <h4 className="font-semibold text-gray-900">
                    {orderMode.type === "pickup"
                      ? "Pickup Location"
                      : "Delivery Address"}
                  </h4>
                </div>
                <MapLocationSelector
                  selectedLocation={selectedLocation}
                  onLocationSelect={handleLocationSelect}
                />
                {/* {selectedLocation && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600 bg-emerald-50 p-3 rounded-lg">
                    <Navigation className="w-4 h-4 text-emerald-600" />
                    <span>
                      {selectedLocation.distance} away •{" "}
                      {selectedLocation?.isOpen ? "Open" : "Closed"}
                    </span>
                  </div>
                )} */}
              </div>

              {/* Date/Time Picker - Enhanced */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">
                    Schedule Order
                  </h4>
                </div>
                <DateTimePicker
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  onDateTimeChange={handleDateTimeChange}
                  openingTime="10:45 AM"
                  closingTime="9:00 PM"
                  slotInterval={30}
                />
                {/* <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span>
                      {orderMode.type === "pickup"
                        ? "Ready for pickup"
                        : "Estimated delivery"}
                      : {selectedTime}
                    </span>
                  </div>
                </div> */}
              </div>
            </div>
          </div>

          {/* Search and Categories - Enhanced Mobile Layout */}
          {/* <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-2">
              <Search className="w-4 h-4 text-gray-600" />
              <h4 className="font-semibold text-gray-900">Find Your Food</h4>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="w-full sm:w-64">
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger className="h-12 sm:h-14 bg-white border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem
                        key={category}
                        value={category}
                        className="font-medium py-3"
                      >
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="relative w-full sm:flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search for dishes, ingredients, or flavors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 sm:h-14 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 bg-white shadow-sm hover:shadow-md transition-all duration-200 text-base"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {["Pad Thai", "Green Curry", "Tom Yum", "Mango Sticky Rice"].map(
                (suggestion) => (
                  <Button
                    key={suggestion}
                    variant="outline"
                    size="sm"
                    onClick={() => setSearchQuery(suggestion)}
                    className="text-xs sm:text-sm px-3 py-1 h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 transition-all duration-200"
                  >
                    {suggestion}
                  </Button>
                )
              )}
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}
